import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ProBadge } from 'components/icons';
import { subscriptionSelector, subtierSelector } from 'redux/modules/pricing';
import { SubscriptionFeatures } from 'redux/modules/pricing/types';
import { State } from 'redux/types';
import { DEFAULT_BLACKLIST } from './constants';
import { ProIndicatorBlacklist, ProIndicatorFeature } from './types';

export interface UseProIndicatorConfig {
  blacklist?: ProIndicatorBlacklist;
  feature?: ProIndicatorFeature;
}

interface UseProIndicatorResult {
  Badge: typeof ProBadge | null;
  isFree: boolean;
}

const NoBadge: typeof ProBadge = () => null;

function featureToStateKey<TFeature extends ProIndicatorFeature>(
  feature: TFeature,
) {
  return `${feature}Enabled` as keyof SubscriptionFeatures;
}

export default function useProIndicator({
  blacklist = DEFAULT_BLACKLIST,
  feature,
}: UseProIndicatorConfig = {}): UseProIndicatorResult {
  const { pauseSchedule } = useSelector(subscriptionSelector) || {};
  const subtier = useSelector(subtierSelector);

  const hasFeatureAccess = useSelector<State, boolean>(
    useCallback(
      state => {
        return !feature
          ? undefined
          : subscriptionSelector(state).get(featureToStateKey(feature));
      },
      [feature],
    ),
  );

  const pauseStatus = pauseSchedule?.pauseStatus ?? null;
  const isPlanPaused = pauseStatus === 'active';
  const hasSubtierAccess = !blacklist.includes(subtier) && !isPlanPaused;

  const hasAccess = feature !== undefined ? hasFeatureAccess : hasSubtierAccess;

  const Badge = useMemo(() => (!hasAccess ? ProBadge : NoBadge), [hasAccess]);

  return {
    Badge,
    isFree: !hasAccess,
  };
}
